import { useAuth0 } from '@auth0/auth0-react'
import { Menu, MenuItem } from '@rgrd/component-library'

import { useCanLogout } from '~/shell/auth/useCanLogout'

import styles from './UserMenu.module.css'

/**
 * Derives up-to-two-letter initials from an Auth0 profile, falling back to a
 * neutral glyph when no usable name or email is present.
 */
const getInitials = (name?: string, email?: string): string => {
  const source = name?.trim() || email?.trim()
  if (!source) {
    return '—'
  }

  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
}

/**
 * The header avatar and its user menu (AUTH-09).
 *
 * The avatar shows the signed-in user's initials. It is an interactive dropdown
 * only when there is at least one action to offer — today that is **Log out**,
 * gated by {@link useCanLogout}, which is always true in Scout. With no actions
 * the avatar is a plain, non-interactive initials circle.
 *
 * This subsumes the standalone `LogoutButton`: logout now lives behind the avatar
 * rather than in the page body.
 */
export const UserMenu = () => {
  const { user, logout } = useAuth0()
  const canLogout = useCanLogout()
  const initials = getInitials(user?.name, user?.email)

  if (!canLogout) {
    return (
      <span className={styles.avatar} aria-hidden="true">
        {initials}
      </span>
    )
  }

  return (
    <Menu
      label="Account"
      triggerLabel="Account"
      align="end"
      triggerClassName={styles.trigger}
      trigger={() => (
        <span className={styles.avatar} aria-hidden="true">
          {initials}
        </span>
      )}
    >
      <MenuItem
        onSelect={() =>
          logout({ logoutParams: { returnTo: window.location.origin } })
        }
      >
        Log out
      </MenuItem>
    </Menu>
  )
}
